import { useRef } from "react";
import { Sparkles, User } from "lucide-react";
import { privacyOptions } from "../../data/phase3Content";
import { people } from "../../data/demoContent";

const Avatar = ({ p }) => (
  <li className="relative" title={p.name}>
    {p.avatar ? (
      <img src={p.avatar} alt={p.name} className="h-11 w-11 rounded-full border-2 border-white object-cover shadow-sm" />
    ) : (
      <span className="grid h-11 w-11 place-items-center rounded-full border-2 border-white bg-gradient-to-br from-[#A79DE2] to-[#80D9EB] text-[14px] font-bold text-white" aria-label={p.name}>{p.name[0]}</span>
    )}
    {p.ai && <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full bg-[#7866E8] text-white"><Sparkles size={10} aria-hidden /></span>}
  </li>
);

export const PrivacySelector = ({ value, onChange }) => {
  const refs = useRef([]);
  const idx = privacyOptions.findIndex((o) => o.id === value);
  const opt = privacyOptions[idx];

  const onKey = (e) => {
    const d = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (!d) return;
    e.preventDefault();
    const n = (idx + d + privacyOptions.length) % privacyOptions.length;
    onChange(privacyOptions[n].id);
    refs.current[n]?.focus();
  };

  return (
    <div className="glass glass-heavy rounded-[36px] p-5 sm:p-8 lg:p-10" data-testid="privacy-selector">
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-12">
        <div>
          <div className="flex items-center gap-3">
            <img src={people.karna.story.image} alt="Your story preview" className="h-16 w-12 rounded-xl object-cover" />
            <div>
              <p className="text-[15px] font-semibold text-[#08090D]">Who can see your story?</p>
              <p className="text-[13px] text-[#5b5f73]">“{people.karna.story.caption}”</p>
            </div>
          </div>
          <div role="radiogroup" aria-label="Story audience" className="mt-6 grid grid-cols-2 gap-1.5 rounded-[24px] bg-white/55 p-1.5 sm:flex sm:rounded-full" onKeyDown={onKey}>
            {privacyOptions.map((o, i) => (
              <button
                key={o.id}
                ref={(el) => (refs.current[i] = el)}
                type="button"
                role="radio"
                aria-checked={value === o.id}
                tabIndex={value === o.id ? 0 : -1}
                onClick={() => onChange(o.id)}
                data-testid={`privacy-option-${o.id}`}
                className={`h-11 flex-1 whitespace-nowrap rounded-full px-3 text-[13px] font-semibold transition-colors ${value === o.id ? "bg-[#08090D] text-white" : "text-[#3c4052] hover:bg-white/70"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <p className="mt-4 text-[12px] text-[#777B8D]">UI demonstration — no account settings are changed.</p>
        </div>
        <div className="rounded-[28px] bg-white/60 p-5 sm:p-6" aria-live="polite" data-testid="privacy-audience-preview" data-option={opt.id}>
          <div className="flex items-center justify-between gap-3">
            <p className="text-[12px] font-semibold text-[#777B8D]">Audience preview</p>
            {opt.ai && <span className="spc-pill spc-pill--onlight spc-pill--ai" data-testid="privacy-ai-label"><Sparkles size={12} aria-hidden /> Suggested by AI</span>}
          </div>
          <ul className="mt-4 flex min-h-[48px] flex-wrap gap-2">
            <li className="relative">
              <img src={people.karna.avatar} alt="You" className="h-11 w-11 rounded-full border-2 border-[#7866E8] object-cover" />
            </li>
            {opt.audience.map((p) => <Avatar key={p.id} p={p} />)}
            {opt.audience.length === 0 && <li className="flex items-center gap-1.5 text-[13px] text-[#5b5f73]"><User size={14} aria-hidden /> Only you</li>}
          </ul>
          <p className="mt-4 text-[20px] font-bold tracking-[-0.02em] text-[#08090D]" data-testid="privacy-audience-count">{opt.count}</p>
          <p className="mt-1.5 text-[15px] leading-relaxed text-[#3c4052]" data-testid="privacy-audience-explain">{opt.explain}</p>
        </div>
      </div>
    </div>
  );
};
